import {
  useClient,
  useEventListener,
  useRoom,
} from "@liveblocks/react";
import type { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { useAppDispatch } from "@store";
import {
  changeEntityLayout,
  createEntity,
  deleteEntity,
  editEntity,
  handleAnchor,
  handleBroadcastHistory,
  handleCustomColor,
  reorderEntities,
  restoreStoryFromRevision,
  updateDesignSystem,
  updateStoryByKeys,
  updateStorySettings,
} from "@store/story";
import { type ReactNode, useEffect } from "react";
import { toast } from "react-toastify";

declare global {
  interface Window {
    SD_LIVE_ROOM?: ReturnType<typeof useRoom>;
  }
}

export const BroadcastProvider = ({ children }: { children: ReactNode }) => {
  const room = useRoom();
  const client = useClient();
  const dispatch = useAppDispatch();

  useEventListener(({ event }) => {
    const { type, payload, user } = event as {
      type: string;
      payload: any;
      user: { name: string; email: string; color: string; theme: string };
    };
    const updatedPayload = { ...payload, broadcast: true };

    if (type === "handle-undo" || type === "handle-redo") {
      // In a Yjs room undo/redo flows through the UndoManager — the CRDT
      // inverse already arrives via the sync channel. These broadcasts can
      // only come from a legacy-code peer (rolling deploy); applying the
      // hand-rolled Redux undo on top of the Yjs inverse double-applies the
      // step and diverges the peers. Honor them only when the bridge is off.
      if (window.SD_YJS_COLLABORATION?.enabled) return;
      updatedPayload.actionType = type;
    }

    const eventHandlers: Record<string, ActionCreatorWithPayload<any>> = {
      edit: editEntity,
      delete: deleteEntity,
      "handle-colors": handleCustomColor,
      "handle-anchors": handleAnchor,
      "update-story": updateStoryByKeys,
      "update-story-settings": updateStorySettings,
      "reorder-entities": reorderEntities,
      "create-entity": createEntity,
      "handle-undo": handleBroadcastHistory,
      "handle-redo": handleBroadcastHistory,
      "restore-from-revision": restoreStoryFromRevision,
      "update-design-system": updateDesignSystem,
      "change-entity-layout": changeEntityLayout,
    };

    const handler = eventHandlers[type];
    if (handler) dispatch(handler(updatedPayload));

    if (type === "restore-from-revision") {
      toast.info(`${user.name} restored a file version`);
    }
  });

  useEffect(() => {
    window.SD_LIVE_ROOM = room;
    client.resolvers.invalidateMentionSuggestions();
    return () => {
      delete window.SD_LIVE_ROOM;
      // Do NOT call room.disconnect() here. RoomProvider owns the
      // connection lifecycle; manual disconnect on unmount kills the
      // Liveblocks WebSocket for the whole page (kills presence /
      // cursors / Yjs sync), and dev-mode StrictMode remounts then
      // never reopen it because this effect only reads `room`.
    };
  }, [room, client]);

  return <>{children}</>;
};
